import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { TrendingUp, Plus, X, ClipboardList, Hammer, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TABS = ["plans", "activities", "projects"];

export default function Planning() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("plans");
  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">GPDP</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "विकेन्द्रीकृत योजना" : lang === "hr" ? "गाम की योजना" : "Gram Panchayat Development Plan"}
          </h1>
          <p className="mt-1 text-sm text-slate-600">{lang === "hi" ? "योजना → गतिविधि → परियोजना" : "Plans → Activities → Projects"}</p>
        </div>
        <div className="flex border-b border-slate-200 mb-4">
          {TABS.map((tp) => (
            <button key={tp} onClick={() => setTab(tp)} data-testid={`plan-tab-${tp}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 capitalize ${tab === tp ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`}>
              {tp}
            </button>
          ))}
        </div>
        {tab === "plans" && <Plans user={user} />}
        {tab === "activities" && <Activities user={user} />}
        {tab === "projects" && <Projects user={user} />}
      </div>
    </OfficerLayout>
  );
}

function Plans({ user }) {
  const [rows, setRows] = useState([]);
  const load = () => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/gpdp/plans", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="plans-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Plan No.</th><th className="text-left px-4 py-3">Year</th>
              <th className="text-right px-4 py-3">Budget</th><th className="text-left px-4 py-3">Priorities</th>
              <th className="text-left px-4 py-3">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{p.plan_no}</td>
              <td className="px-4 py-3">{p.plan_year}</td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(p.total_budget)}</td>
              <td className="px-4 py-3 text-xs">{p.priorities?.join(", ")}</td>
              <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{p.status}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No plans</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Activities({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/gpdp/activities", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="activities-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Activity</th><th className="text-left px-4 py-3">Sector</th>
              <th className="text-right px-4 py-3">Est. Cost</th><th className="text-left px-4 py-3">Funding</th>
              <th className="text-left px-4 py-3">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className="px-4 py-3"><div className="font-medium">{a.name}</div><div className="text-xs text-slate-500">{a.description}</div></td>
              <td className="px-4 py-3 capitalize">{a.sector}</td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(a.estimated_cost)}</td>
              <td className="px-4 py-3 text-xs">{a.funding_source || "—"}</td>
              <td className="px-4 py-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">{a.status}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No activities</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Projects({ user }) {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  const load = () => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/projects", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="projects-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr><th className="text-left px-4 py-3">Project</th><th className="text-left px-4 py-3">Scheme</th>
                <th className="text-right px-4 py-3">Budget</th><th className="text-left px-4 py-3">Progress</th>
                <th className="text-left px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} onClick={() => setPicked(p)}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${picked?.id === p.id ? "bg-[#0A3161]/5" : ""}`}>
                <td className="px-4 py-3"><div className="font-medium">{p.name}</div><div className="font-mono text-xs text-slate-500">{p.project_no}</div></td>
                <td className="px-4 py-3 text-xs">{p.scheme || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(p.budget_total)}</td>
                <td className="px-4 py-3 min-w-[140px]">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#107C10]" style={{ width: `${p.physical_progress_pct}%` }} />
                    </div>
                    <span className="text-xs font-mono w-9 text-right">{p.physical_progress_pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 capitalize text-xs">{p.status?.replace("_", " ")}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No projects</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5">
        {picked ? (
          <ProgressEditor project={picked} onSaved={() => { load(); }} />
        ) : (
          <div className="text-sm text-slate-500 py-10 text-center">Select a project to update progress</div>
        )}
      </div>
    </div>
  );
}

function ProgressEditor({ project, onSaved }) {
  const [phys, setPhys] = useState(project.physical_progress_pct || 0);
  const [fin, setFin] = useState(project.financial_progress_pct || 0);
  const [spent, setSpent] = useState(project.fund_spent || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPhys(project.physical_progress_pct || 0);
    setFin(project.financial_progress_pct || 0);
    setSpent(project.fund_spent || 0);
  }, [project.id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/progress`, {
        physical_progress_pct: Number(phys),
        financial_progress_pct: Number(fin),
        fund_spent: Number(spent),
      });
      toast.success("Progress updated");
      onSaved();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="progress-editor">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">Progress Update</div>
      <h3 className="font-heading text-lg font-bold mt-1">{project.name}</h3>
      <div className="font-mono text-xs text-slate-500 mt-0.5">{project.project_no}</div>
      <div className="mt-4 space-y-3 text-sm">
        <Field label="Physical Progress (%)">
          <input type="number" min="0" max="100" value={phys} onChange={(e) => setPhys(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="progress-phys" />
        </Field>
        <Field label="Financial Progress (%)">
          <input type="number" min="0" max="100" value={fin} onChange={(e) => setFin(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
        </Field>
        <Field label="Fund Spent (₹)">
          <input type="number" value={spent} onChange={(e) => setSpent(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono" />
        </Field>
        <button onClick={save} disabled={busy} data-testid="progress-save"
          className="w-full px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
          {busy ? "…" : "Save Progress"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">{label}</div>{children}</div>
);
